import React, { useState, useCallback } from 'react'
import type { Content, ContentDetail, ContentStatus, Collection } from './types'
import ContentCard from './ContentCard'
import ContentDetailModal from './ContentDetailModal'
import DeleteConfirmModal from './DeleteConfirmModal'
import ContentUploadPanel from '../ContentUploadPanel'
import { useWordPressImport, WordPressImportDrawer } from './wordpress'

type ContentsPanelProps = {
  contents: Content[]
  collections: Collection[]
  loading: boolean
  panelTitle: string
  selectedCollectionId: number | 'orphan' | null
  workingIds: Record<number, string>
  statusFilter: ContentStatus | 'all'
  onStatusFilterChange: (status: ContentStatus | 'all') => void
  onReload: () => Promise<void>
  onRemove: (id: number) => Promise<boolean>
  onReindex: (id: number) => Promise<boolean>
  onForceTranscribe: (id: number) => Promise<boolean>
  onGetDetail: (id: number) => Promise<ContentDetail | null>
  onDownloadTranscription: (id: number) => Promise<void>
  onUpdateCollection: (id: number, collectionId: number | null) => Promise<boolean>
  uploadOpen?: boolean
  onUploadOpenChange?: (open: boolean) => void
}

/**
 * Panel principal des contenus
 * Responsabilité unique : liste et gestion des contenus
 */
export default function ContentsPanel({
  contents,
  collections,
  loading,
  panelTitle,
  selectedCollectionId,
  workingIds,
  statusFilter,
  onStatusFilterChange,
  onReload,
  onRemove,
  onReindex,
  onForceTranscribe,
  onGetDetail,
  onDownloadTranscription,
  onUpdateCollection,
  uploadOpen: uploadOpenProp,
  onUploadOpenChange,
}: ContentsPanelProps) {
  const [uploadOpenInternal, setUploadOpenInternal] = useState(false)
  const uploadOpen = uploadOpenProp ?? uploadOpenInternal
  const setUploadOpen = (open: boolean) => {
    setUploadOpenInternal(open)
    onUploadOpenChange?.(open)
  }
  const [detailContentId, setDetailContentId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Content | null>(null)

  // WordPress Import - pré-sélectionner la collection si une est choisie
  const wpImport = useWordPressImport({
    onImportComplete: () => onReload(),
    initialCollectionId: typeof selectedCollectionId === 'number' ? selectedCollectionId : null,
  })

  const handleUploadComplete = useCallback(() => {
    setUploadOpen(false)
    onReload()
  }, [onReload])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return false
    const success = await onRemove(deleteTarget.id)
    return success
  }, [deleteTarget, onRemove])

  const handleReindex = useCallback(async (id: number) => {
    await onReindex(id)
    // Refresh detail if open
    if (detailContentId === id) {
      // Force refresh by closing and reopening
      setDetailContentId(null)
      setTimeout(() => setDetailContentId(id), 100)
    }
  }, [onReindex, detailContentId])

  const handleForceTranscribe = useCallback(async (id: number) => {
    await onForceTranscribe(id)
    if (detailContentId === id) {
      setDetailContentId(null)
      setTimeout(() => setDetailContentId(id), 100)
    }
  }, [onForceTranscribe, detailContentId])

  return (
    <>
      <main className="flex-1 min-w-0">
        <div className="space-y-4 rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{panelTitle}</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {contents.length} contenu{contents.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Filtre statut */}
              <select
                value={statusFilter}
                onChange={e => onStatusFilterChange(e.target.value as ContentStatus | 'all')}
                className="rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:text-zinc-200 dark:bg-zinc-900/50"
              >
                <option value="all">Tous les statuts</option>
                <option value="indexed">Indexés</option>
                <option value="transcribed">Transcrits</option>
                <option value="uploaded">En attente</option>
                <option value="transcribing">Transcription...</option>
                <option value="indexing">Indexation...</option>
                <option value="error">En erreur</option>
              </select>
              <button
                type="button"
                onClick={wpImport.open}
                className="flex items-center gap-2 rounded-full border border-blue-200/70 bg-blue-50/80 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
                <span className="hidden sm:inline">WordPress</span>
              </button>
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
                data-onboarding="add-content-btn"
              >
                + Contenu
              </button>
            </div>
          </div>

          {/* Liste des contenus */}
          <div className="space-y-3">
            {loading ? (
              <div className="animate-pulse space-y-3 py-4">
                <div className="h-20 rounded-2xl bg-emerald-100/60 dark:bg-emerald-900/40" />
                <div className="h-20 rounded-2xl bg-emerald-100/50 dark:bg-emerald-900/30" />
                <div className="h-20 rounded-2xl bg-emerald-100/40 dark:bg-emerald-900/20" />
              </div>
            ) : contents.length === 0 ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center dark:bg-emerald-900/40">
                  <svg className="h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-700 dark:text-zinc-200">Aucun contenu</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  {selectedCollectionId === 'orphan' 
                    ? 'Tous vos contenus sont associés à une collection.'
                    : 'Ajoutez votre premier contenu pour enrichir cette collection.'}
                </p>
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="mt-4 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
                >
                  + Ajouter un contenu
                </button>
              </div>
            ) : (
              contents.map(content => (
                <ContentCard
                  key={content.id}
                  content={content}
                  isWorking={!!workingIds[content.id]}
                  workingAction={workingIds[content.id]}
                  onViewDetail={() => setDetailContentId(content.id)}
                  onReindex={() => handleReindex(content.id)}
                  onForceTranscribe={() => handleForceTranscribe(content.id)}
                  onDelete={() => setDeleteTarget(content)}
                />
              ))
            )}
          </div>
        </div>
      </main>

      {/* Modal Upload */}
      <ContentUploadPanel
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={handleUploadComplete}
      />

      {/* Modal Détails */}
      <ContentDetailModal
        open={detailContentId !== null}
        contentId={detailContentId}
        collections={collections}
        onClose={() => setDetailContentId(null)}
        onLoadDetail={onGetDetail}
        onDownloadTranscription={onDownloadTranscription}
        onForceTranscribe={async (id) => {
          const success = await onForceTranscribe(id)
          if (success) {
            // Refresh detail
            setDetailContentId(null)
            setTimeout(() => setDetailContentId(id), 100)
          }
          return success
        }}
        onReindex={async (id) => {
          const success = await onReindex(id)
          if (success) {
            setDetailContentId(null)
            setTimeout(() => setDetailContentId(id), 100)
          }
          return success
        }}
        onUpdateCollection={onUpdateCollection}
        isWorking={detailContentId !== null && !!workingIds[detailContentId]}
      />

      {/* Modal Suppression */}
      <DeleteConfirmModal
        open={deleteTarget !== null}
        title="Supprimer le contenu"
        itemName={deleteTarget?.title || ''}
        itemId={deleteTarget?.id || 0}
        warningMessage="Les transcriptions, embeddings et métadonnées seront définitivement supprimés."
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* WordPress Import Drawer */}
      <WordPressImportDrawer
        isOpen={wpImport.isOpen}
        step={wpImport.state.step}
        selectedType={wpImport.state.selectedType}
        selectedItems={wpImport.state.selectedItems}
        contentTypes={wpImport.contentTypes}
        contents={wpImport.contents}
        collections={collections}
        quota={wpImport.quota}
        estimate={wpImport.estimate}
        importStatus={wpImport.importStatus}
        total={wpImport.total}
        hasMore={wpImport.hasMore}
        loading={wpImport.loading}
        loadingContents={wpImport.loadingContents}
        error={wpImport.state.error}
        onClose={wpImport.close}
        onSelectType={wpImport.selectType}
        onToggleItem={wpImport.toggleItem}
        onSelectAll={wpImport.selectAll}
        onSelectNone={wpImport.selectNone}
        onLoadMore={wpImport.loadMore}
        onConfirm={wpImport.goToConfirmation}
        onBackToSelection={wpImport.backToSelection}
        onBackToTypes={wpImport.backToTypes}
        onStartImport={wpImport.startImport}
        onCancelImport={wpImport.cancelImport}
        targetCollectionId={wpImport.state.targetCollectionId}
        autoCreateCollections={wpImport.state.autoCreateCollections}
        onSetTargetCollection={wpImport.setTargetCollection}
        onSetAutoCreateCollections={wpImport.setAutoCreateCollections}
      />
    </>
  )
}
